import React, { ReactNode, FC, useMemo, useCallback, useContext, useEffect, ReactElement } from 'react';
import { Tab as ReactTab, TabList, TabPanel } from 'react-tabs';

import { ParametersExceptFirst } from 'ia-react-core/~/interfaces/ParametersExceptFirst';
import Router from 'ia-react-core/~/services/router/Router';
import selectTabById from '../../../actions/tabs-fn/selectTabById';
import COLOR_SCHEMES from '../../../constants/COLOR_SCHEMES';
import ColorSchemeContext from '../../colorScheme/ColorSchemeContext';
import { StyledTabs } from './Tabs.styles';

import filterChildrenByType from '../../../utils/components/_filterChildrenByType';
import Tab from '../Tab';
import { getTabNameFromUrl, setTabNameToUrl } from './Tabs.utils';

export interface TabsProps {
  /** The name of the Tabs. If provided, it will be used as the reducer key. Otherwise the reducer key will be "Tabs${tabsId}". */
  name?: string;
  /** Gives the focus to the tabs on render. */
  defaultFocus?: boolean;
  /** Always render the tabs even . */
  forceRenderTabPanel?: boolean;
  /** Use a compact version fo the tab. */
  compact?: boolean;
  /** Use a smaller version fo the tab. */
  small?: boolean;
  /** Use a large version fo the tab. */
  large?: boolean;
  /** When used in *uncontrolled* mode, specifies the initial tab to select. */
  initialSelectedTab?: string | number;
  /** Controls the selected tab. Marks this tabs as being "controlled". A controlled tabs won't integrate with Redux nor listen to actions. */
  selectedTab?: string | number;
  /** Callback when the selected tab changes. */
  onSelect?: (index: number, lastIndex?: number, event?: Event) => boolean | void;
  /** A list of [Tab](/Core/Components/Tabs/Tab). */
  children?: ReactNode;
  /** Put small padding for mobile device. */
  paddingXs?: string;
  /** Put normal padding for other devices then mobile. */
  paddingSm?: string;
  /** Select the active tab from a querystring param. */
  selectTabFromUrl?: boolean;
  /** Method used on the "history". Use "push" if you want the navigate through the previous tab selection when clicking the browser "back" button. */
  selectTabFromUrlMethod?: 'push' | 'replace';
  /** Name of the querystring param to select the active tab from when "selectTabFromUrl" is true. By default, the param name is the "name" of the Tabs. */
  selectTabFromUrlParam?: string;
  /** Customize the color of the selected tab and tab content. */
  background?: string;
  /** Customize the color of the selected text. */
  selectedColor?: string;
  /** Customize the color of the selected border and text. */
  selectedTabBorder?: string;
  /** Customize the color of the unselected tab text. */
  unselectedTabColor?: string;
  /** Customize the color of the unselected tab background. */
  unselectedTabBackground?: string;
  /** Customize the color of the unselected tab text. */
  selectedTabBorderSize?: number;
  /** Customize the color of the hovered tab. */
  hoverBackground?: number;
  /** Customize the tab flex behavior. */
  flex?: string;
  /** Customize the tab font-weight. */
  fontWeight?: string;
  /** Customize the border surrounding the tabs. */
  tabBorder?: string;
  /** Tabs margin. */
  tabMargin?: string;
}

interface TabsInternalProps {
  /** @ignore */
  tabsId: number;
  /** @ignore */
  isControlled: boolean;
  /** @ignore */
  dispatch: any;
  /** @ignore */
  scope: string[];
  /** @ignore */
  store: object;
}

export interface TabsComposedProps extends TabsProps, TabsInternalProps {}

const TabsDefaultProps: Partial<TabsProps> = {
  defaultFocus: false,
  forceRenderTabPanel: false,
  paddingXs: '36px 16px 36px 16px',
  paddingSm: '36px 32px 36px 32px',
  selectTabFromUrlMethod: 'replace',
  selectedTabBorderSize: 4,
  fontWeight: 'bold',
};

const Tabs: FC<TabsComposedProps> = ({
  name, tabsId, initialSelectedTab, children, selectedTab, selectTabFromUrl, selectTabFromUrlParam, selectTabFromUrlMethod, isControlled, compact, small, large, paddingXs, paddingSm, dispatch, store, scope, background, selectedColor, selectedTabBorder, unselectedTabColor, unselectedTabBackground, selectedTabBorderSize, hoverBackground, flex, fontWeight, tabBorder, tabMargin, onSelect, ...props
}) => {
  const { scheme } = useContext(ColorSchemeContext);
  const tabs: ReactElement[] = filterChildrenByType(children, Tab);
  const dispatchSelectTab = useCallback((...args: ParametersExceptFirst<typeof selectTabById>) => dispatch(selectTabById(tabsId, ...args)), [dispatch, tabsId]);
  const history = Router.useHistory();
  const location = Router.useLocation();

  const defaultTab = useMemo(() => initialSelectedTab || 0, [initialSelectedTab]);
  const querystringParam = useMemo(() => selectTabFromUrlParam || name, [name, selectTabFromUrlParam]);

  const findTabIndexByName = useCallback((tabName: string) => {
    const index = tabs.findIndex((tab) => tab.props.name === tabName);
    if (index !== -1) return index;
    const [, indexFromName] = tabName?.match(/^tab([\d]])$/) || [];
    return indexFromName ?? -1;
  }, [tabs]);

  const resolvedSelectedTabIndex = useMemo(() => {
    if (typeof selectedTab === 'number') {
      return selectedTab;
    }

    const tabIndex = findTabIndexByName(selectedTab);

    if (tabIndex !== -1) {
      return tabIndex;
    }

    return defaultTab;
  }, [defaultTab, findTabIndexByName, selectedTab]);

  const handleSelect = useCallback((index: number, lastIndex: number, event: Event) => {
    if (!isControlled) {
      dispatchSelectTab(index);
    }

    if (typeof onSelect !== 'undefined') {
      onSelect(index, lastIndex, event);
    }

    if (selectTabFromUrl) {
      const tabName: string = tabs[index].props.name || `tab${index}`;
      setTabNameToUrl(tabName, history, { querystringParam, selectTabFromUrlMethod });
    }
  }, [dispatchSelectTab, history, isControlled, onSelect, querystringParam, selectTabFromUrl, selectTabFromUrlMethod, tabs]);

  useEffect(() => {
    if (selectTabFromUrl) {
      const selectedTabFromUrl = getTabNameFromUrl(querystringParam);
      if (selectedTabFromUrl) {
        const selectedTabFromUrlIndex = findTabIndexByName(selectedTabFromUrl);
        if (selectedTabFromUrlIndex !== -1 && selectedTabFromUrlIndex !== resolvedSelectedTabIndex) {
          dispatchSelectTab(selectedTabFromUrlIndex);
        }
      }
    }
  }, [selectTabFromUrl, selectTabFromUrlParam, querystringParam, location, findTabIndexByName, resolvedSelectedTabIndex, dispatchSelectTab]);

  const sizeClassName = useMemo(() => {
    if (compact) return 'ia-react-core_tabs--compact';
    if (small) return 'ia-react-core_tabs--small';
    if (large) return 'ia-react-core_tabs--large';
    return '';
  }, [compact, small, large]);

  const lightClassName = useMemo(() => {
    if (scheme === COLOR_SCHEMES.LIGHT) return 'ia-react-core_tabs--light';
    return 'ia-react-core_tabs--standard';
  }, [scheme]);

  return (
    <StyledTabs
      data-testid="Tabs"
      selectedIndex={resolvedSelectedTabIndex}
      small={small}
      large={large}
      paddingXs={paddingXs}
      paddingSm={paddingSm}
      className={`react-tabs ${lightClassName}`}
      $background={background}
      $selectedColor={selectedColor}
      $selectedTabBorder={selectedTabBorder}
      $unselectedTabColor={unselectedTabColor}
      $unselectedTabBackground={unselectedTabBackground}
      $selectedTabBorderSize={selectedTabBorderSize}
      $hoverBackground={hoverBackground}
      $flex={flex}
      $fontWeight={fontWeight}
      $tabBorder={tabBorder}
      $tabMargin={tabMargin}
      {...props}
      onSelect={handleSelect}
    >
      <TabList>
        {tabs.map((tab, index) => (
          <ReactTab
            key={index}
            disabled={tab.props.disabled}
            tabIndex={String(tab.props.tabIndex || '0')}
            data-test-name={tab.props.name}
            className={`react-tabs__tab ${sizeClassName}`}
            onClick={tab.props.onClick}
          >
            {tab.props.title}
          </ReactTab>
        ))}
      </TabList>
      {tabs.map((tab, index) => (
        <TabPanel
          data-test-name={tab.props.name}
          forceRender={tab.props.forceRender}
          className={`react-tabs__tab-panel ${sizeClassName}`}
          key={index}
        >
          {tab.props.children}
        </TabPanel>
      ))}
    </StyledTabs>
  );
};

Tabs.defaultProps = TabsDefaultProps;

export default Tabs;
